/** Card entry — Maverick Hosted Fields when live/sandbox (card data never touches MSC), plain test form in simulated mode. */
import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { btnPrimary, errorText, inputCls } from '../finance/financeUi';

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (window.HostedFields) return resolve();
    const s = document.createElement('script');
    s.src = src; s.onload = resolve; s.onerror = () => reject(new Error('Could not load the secure card form'));
    document.body.appendChild(s);
  });
}

function HostedCard({ session, onSaved }) {
  const formRef = useRef(null);
  const [msg, setMsg] = useState('Loading secure card form…');
  useEffect(() => {
    let off = false;
    loadScript(session.script).then(() => {
      if (off) return;
      const form = window.HostedFields.create({ token: session.token, amount: 0, fields: {
        cardNumber: { target: '#mv-card-number' }, cardExpiration: { target: '#mv-card-exp' }, cardCvv: { target: '#mv-card-cvv' },
        cardHolderName: { target: '#mv-card-name' }, submit: { target: '#mv-card-submit' } } });
      formRef.current = form;
      form.addEventListener('hostedFields.ready', () => setMsg(''));
      form.addEventListener('hostedFields.error', (e) => setMsg(e.detail?.message || 'Card form error'));
      form.addEventListener('submit.result', async (e) => {
        if (!e.detail?.result) return;
        try { onSaved(await api.request('/me/billing/card', { method: 'POST', body: JSON.stringify({ hosted_token: session.token }) })); } catch (err) { toast.error(errorText(err)); }
      });
    }).catch((e) => setMsg(e.message));
    return () => { off = true; };
  }, [session, onSaved]);
  return (
    <div className="space-y-2" data-testid="hosted-card-form">
      {['mv-card-name', 'mv-card-number', 'mv-card-exp', 'mv-card-cvv'].map((id) => <div key={id} id={id} className="min-h-10" />)}
      <div id="mv-card-submit" />
      {msg && <p className="text-xs text-muted-foreground" data-testid="hosted-card-msg">{msg}</p>}
    </div>
  );
}

function TestCard({ onSaved }) {
  const [f, setF] = useState({ name: '', card_number: '', exp: '', cvv: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { onSaved(await api.request('/me/billing/card', { method: 'POST', body: JSON.stringify(f) })); } catch (err) { toast.error(errorText(err)); } finally { setBusy(false); }
  };
  return (
    <form className="space-y-2" onSubmit={save} data-testid="test-card-form">
      <input className={inputCls} placeholder="Name on card" value={f.name} onChange={set('name')} data-testid="card-name" />
      <input className={inputCls} placeholder="Card number" inputMode="numeric" value={f.card_number} onChange={set('card_number')} data-testid="card-number" />
      <div className="grid grid-cols-2 gap-2">
        <input className={inputCls} placeholder="MM/YY" value={f.exp} onChange={set('exp')} data-testid="card-exp" />
        <input className={inputCls} placeholder="CVV" inputMode="numeric" value={f.cvv} onChange={set('cvv')} data-testid="card-cvv" />
      </div>
      <p className="text-xs text-muted-foreground">Test mode: use 4111 1111 1111 1111 (approves) or 4000 0000 0000 0002 (declines). Only the last 4 digits are kept.</p>
      <button type="submit" className={btnPrimary} disabled={busy} data-testid="card-save-btn">Save card</button>
    </form>
  );
}

export function CardForm({ onSaved }) {
  const [session, setSession] = useState(null);
  useEffect(() => { api.request('/me/billing/card-session', { method: 'POST' }).then(setSession).catch((e) => toast.error(errorText(e))); }, []);
  if (!session) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return session.mode === 'simulated' ? <TestCard onSaved={onSaved} /> : <HostedCard session={session} onSaved={onSaved} />;
}
